import { TEntityRef } from '@ws/shared/types';
import { FormEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { BusEventType, eventBus } from '../../utils/EventBus';

export const NAME_UPDATE_TIMEOUT = 1500;

// Сравниваем и тип: onSave адресует узел целиком (TEntityRef), и при совпадении id, но
// другом типе правка ушла бы не в ту карту сущностей.
const isSameOwner = (a: TEntityRef | null, b: TEntityRef | null) =>
  a?.id === b?.id && a?.type === b?.type;

interface IUseDebouncedNameSaveParams {
  /** Чьё имя редактируется. Сохранение всегда адресуется ЭТОМУ узлу. */
  owner: TEntityRef | null;
  /** Имя узла в сторе: сюда же приезжают внешние (remote) переименования. */
  currentName: string;
  onSave(nameToSave: string, owner: TEntityRef): void;
}

export interface IDebouncedNameSave {
  name: string;
  onChange(event: FormEvent<HTMLInputElement>): void;
  onBlur(): void;
}

/**
 * Ввод имени узла с отложенным сохранением.
 *
 * Форма намеренно повторяет SaveManagerExtension, включая ref'ы владельца и колбэка:
 * ровно те же грабли (правка уходит в ЧУЖОЙ узел или теряется) уже ловились в редакторе
 * содержимого (F19, WSL-4/5). Заголовки при переключении заметки НЕ размонтируются,
 * поэтому владелец обязателен: без него отложенное имя заметки A запишется в заметку B.
 */
export function useDebouncedNameSave({
  owner,
  currentName,
  onSave,
}: IUseDebouncedNameSaveParams): IDebouncedNameSave {
  const [name, setName] = useState(currentName);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestNameRef = useRef(currentName);
  const wasChangedRef = useRef(false);
  // Имя, которое уже лежит в сторе: пустую правку (набрал и стёр) не отправляем — иначе в
  // журнал событий попадёт no-op переименование, а для корня ещё и updateProjectMeta.
  const savedNameRef = useRef(currentName);
  const ownerRef = useRef(owner);

  // Колбэк потребителя пересоздаётся каждым рендером и замыкает текущий выбранный узел.
  // Через ref его читают таймер и flush, но адресата берут из ownerRef, а не из замыкания.
  const onSaveRef = useRef(onSave);
  useLayoutEffect(() => {
    onSaveRef.current = onSave;
  });

  const clearOwnTimer = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const saveWith = useCallback(
    (nameToSave: string, targetOwner: TEntityRef | null) => {
      wasChangedRef.current = false;
      clearOwnTimer();

      if (targetOwner) {
        savedNameRef.current = nameToSave;
        onSaveRef.current(nameToSave, targetOwner);
      }
    },
    [clearOwnTimer],
  );

  const flushPendingSave = useCallback(() => {
    if (!wasChangedRef.current) {
      return;
    }

    if (latestNameRef.current === savedNameRef.current) {
      wasChangedRef.current = false;
      clearOwnTimer();
      return;
    }

    saveWith(latestNameRef.current, ownerRef.current);
  }, [saveWith, clearOwnTimer]);

  // Смена узла: несохранённое имя уходит в ПРЕЖНИЙ узел, затем поле показывает новый.
  useEffect(() => {
    if (isSameOwner(ownerRef.current, owner)) {
      return;
    }

    // ownerRef здесь ещё указывает на ПРЕЖНИЙ узел — flush адресует правку ему, и через
    // общую проверку: «набрал и стёр» перед переключением не даёт no-op переименования.
    flushPendingSave();

    clearOwnTimer();
    ownerRef.current = owner;
    latestNameRef.current = currentName;
    savedNameRef.current = currentName;
    wasChangedRef.current = false;
    setName(currentName);
  }, [owner, currentName, flushPendingSave, clearOwnTimer]);

  // Внешнее переименование того же узла: зеркалим, чтобы blur и flush не записали обратно
  // доисторическое имя.
  useEffect(() => {
    if (!isSameOwner(ownerRef.current, owner)) {
      return;
    }

    if (!wasChangedRef.current) {
      latestNameRef.current = currentName;
      savedNameRef.current = currentName;
      setName(currentName);
    }
  }, [currentName, owner]);

  const handleChange = useCallback(
    (event: FormEvent<HTMLInputElement>) => {
      const newName = (event.target as HTMLInputElement).value;

      setName(newName);
      latestNameRef.current = newName;
      wasChangedRef.current = true;

      clearOwnTimer();
      // Через flushPendingSave, а не saveWith: таймер — самый частый путь сохранения, и
      // проверка «имя не изменилось» обязана стоять и на нём (WSL-19).
      timer.current = setTimeout(() => {
        flushPendingSave();
      }, NAME_UPDATE_TIMEOUT);
    },
    [clearOwnTimer, flushPendingSave],
  );

  const handleBlur = useCallback(() => {
    flushPendingSave();
  }, [flushPendingSave]);

  // Уход со страницы (барьер) и размонтирование — те же «концы правки», что и blur.
  useEffect(() => {
    const handleFlushRequest = () => {
      flushPendingSave();
    };

    eventBus.on(BusEventType.EDITOR_FLUSH_PENDING_SAVES, handleFlushRequest);

    return () => {
      eventBus.off(BusEventType.EDITOR_FLUSH_PENDING_SAVES, handleFlushRequest);
    };
  }, [flushPendingSave]);

  useEffect(
    () => () => {
      flushPendingSave();
      clearOwnTimer();
    },
    [flushPendingSave, clearOwnTimer],
  );

  // Закрытие вкладки — тоже уход, и его барьер не покрывает: страница не размонтируется
  // через React. Тот же обработчик есть у редактора содержимого.
  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!wasChangedRef.current) {
        return;
      }

      flushPendingSave();
      event.preventDefault();
      // eslint-disable-next-line no-param-reassign
      event.returnValue = 'Wait until saving..';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [flushPendingSave]);

  return { name, onChange: handleChange, onBlur: handleBlur };
}
